/**
 * FormulaEditor — modal dialog for creating and editing formula definitions.
 *
 * Props
 * -----
 * formula       : existing FormulaOut | null   (null → create mode)
 * sessionId     : string                       (used for live preview)
 * selectedLaps  : number[]                     (first lap used for preview)
 * onSave        : (saved: FormulaOut) => void
 * onClose       : () => void
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { createFormula, updateFormula, getFormulaTrace, getFormulaAliases } from '../../services/api';
import styles from './FormulaEditor.module.css';

const EXAMPLE_FUNCTIONS = [
  { label: 'sqrt(…)',            insert: 'sqrt()',                tip: 'Square root' },
  { label: 'abs(…)',             insert: 'abs()',                 tip: 'Absolute value' },
  { label: 'diff(…, dist)',      insert: 'diff(, dist)',          tip: 'Derivative w.r.t. distance' },
  { label: 'diff(…, time)',      insert: 'diff(, time)',          tip: 'Derivative w.r.t. time' },
  { label: 'integrate(…, dist)', insert: 'integrate(, dist)',     tip: 'Cumulative integral w.r.t. distance' },
  { label: 'integrate(…, time)', insert: 'integrate(, time)',     tip: 'Cumulative integral w.r.t. time' },
  { label: 'rolling_mean(…, n)', insert: 'rolling_mean(, 10)',    tip: 'Moving average over n samples' },
  { label: 'rolling_rms(…, n)',  insert: 'rolling_rms(, 10)',     tip: 'RMS over n samples' },
  { label: 'normalize(…)',       insert: 'normalize()',           tip: 'Scale to [0, 1]' },
  { label: 'clip(…, lo, hi)',    insert: 'clip(, 0, 1)',          tip: 'Clamp to range' },
];

const PREVIEW_DEBOUNCE_MS = 400;

export default function FormulaEditor({ formula, sessionId, selectedLaps, onSave, onClose }) {
  const isEdit = !!formula;

  const [name,       setName]       = useState(formula?.name       ?? '');
  const [expression, setExpression] = useState(formula?.expression ?? '');
  const [unit,       setUnit]       = useState(formula?.unit       ?? '');
  const [color,      setColor]      = useState(formula?.color      ?? '#4f9cf5');
  const [desc,       setDesc]       = useState(formula?.description ?? '');

  const [aliases,    setAliases]    = useState([]);
  const [preview,    setPreview]    = useState(null);   // { ok: bool, message: string }
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);

  const exprRef     = useRef(null);
  const previewTimer = useRef(null);

  // Load available aliases for this session
  useEffect(() => {
    if (!sessionId) return;
    getFormulaAliases(sessionId).then(r => setAliases(r.aliases || [])).catch(() => {});
  }, [sessionId]);

  // Live preview — fires 400 ms after the user stops typing
  const triggerPreview = useCallback((expr) => {
    clearTimeout(previewTimer.current);
    if (!expr.trim() || !sessionId || selectedLaps.length === 0) {
      setPreview(null);
      return;
    }
    previewTimer.current = setTimeout(async () => {
      const lapIndex = selectedLaps[0];
      try {
        await getFormulaTrace(sessionId, lapIndex, expr, name || 'preview');
        setPreview({ ok: true, message: '✓ Valid expression' });
      } catch (e) {
        setPreview({ ok: false, message: e.message || 'Invalid expression' });
      }
    }, PREVIEW_DEBOUNCE_MS);
  }, [sessionId, selectedLaps, name]);

  useEffect(() => {
    triggerPreview(expression);
    return () => clearTimeout(previewTimer.current);
  }, [expression, triggerPreview]);

  // Insert a function chip at cursor position in the expression textarea
  const insertAt = (text) => {
    const el = exprRef.current;
    if (!el) { setExpression(e => e + text); return; }
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const next  = expression.slice(0, start) + text + expression.slice(end);
    setExpression(next);
    // Restore cursor inside the first () pair
    setTimeout(() => {
      const parenPos = next.indexOf('(', start) + 1;
      el.focus();
      el.setSelectionRange(parenPos, parenPos);
    }, 0);
  };

  const handleSave = async () => {
    if (!name.trim())       { setError('Name is required'); return; }
    if (!expression.trim()) { setError('Expression is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), expression: expression.trim(), unit: unit.trim() || null, color, description: desc.trim() || null };
      const saved = isEdit
        ? await updateFormula(formula.id, body)
        : await createFormula(body);
      onSave(saved);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={styles.dialog}>
        <div className={styles.header}>
          <span className={styles.title}>{isEdit ? 'Edit Formula' : 'New Formula'}</span>
          <button className={styles.closeBtn} onClick={onClose}>✕</button>
        </div>

        <div className={styles.body}>
          {/* Name + Unit row */}
          <div className={styles.row}>
            <label className={styles.field} style={{ flex: 2 }}>
              <span className={styles.fieldLabel}>Name</span>
              <input
                className={styles.input}
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Gsum"
                autoFocus
              />
            </label>
            <label className={styles.field} style={{ flex: 1 }}>
              <span className={styles.fieldLabel}>Unit (optional)</span>
              <input
                className={styles.input}
                value={unit}
                onChange={e => setUnit(e.target.value)}
                placeholder="e.g. g"
              />
            </label>
            <label className={styles.field} style={{ flex: '0 0 56px' }}>
              <span className={styles.fieldLabel}>Color</span>
              <input
                type="color"
                className={styles.colorInput}
                value={color}
                onChange={e => setColor(e.target.value)}
              />
            </label>
          </div>

          {/* Expression */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Expression</span>
            <textarea
              ref={exprRef}
              className={styles.exprInput}
              value={expression}
              onChange={e => setExpression(e.target.value)}
              placeholder="e.g. sqrt(lat_g**2 + lon_g**2)"
              rows={3}
              spellCheck={false}
            />
          </label>

          {/* Preview status */}
          {expression.trim() && preview && (
            <div className={`${styles.preview} ${preview.ok ? styles.previewOk : styles.previewErr}`}>
              {preview.message}
            </div>
          )}

          {/* Channel chips */}
          {aliases.length > 0 && (
            <div className={styles.chipSection}>
              <span className={styles.chipLabel}>Channels</span>
              <div className={styles.chips}>
                {aliases.map(a => (
                  <button key={a} className={styles.chip} onClick={() => insertAt(a)} title={`Insert "${a}"`}>
                    {a}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Function chips */}
          <div className={styles.chipSection}>
            <span className={styles.chipLabel}>Functions</span>
            <div className={styles.chips}>
              {EXAMPLE_FUNCTIONS.map(f => (
                <button key={f.label} className={styles.chip} onClick={() => insertAt(f.insert)} title={f.tip}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Optional description */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Description (optional)</span>
            <input
              className={styles.input}
              value={desc}
              onChange={e => setDesc(e.target.value)}
              placeholder="What does this formula measure?"
            />
          </label>

          {error && <div className={styles.errorMsg}>{error}</div>}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
